import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { customerService } from '../../services/api';

// Initial state
const initialState = {
  customers: [],
  customer: null,
  addresses: [],
  contacts: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalCount: 0,
  },
};

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomers(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCustomer = createAsyncThunk(
  'customers/fetchCustomer',
  async (id, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomer(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (data, { rejectWithValue }) => {
    try {
      const response = await customerService.createCustomer(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await customerService.updateCustomer(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { rejectWithValue }) => {
    try {
      await customerService.deleteCustomer(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCustomerAddresses = createAsyncThunk(
  'customers/fetchCustomerAddresses',
  async (id, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomerAddresses(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCustomerContacts = createAsyncThunk(
  'customers/fetchCustomerContacts',
  async (id, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomerContacts(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Customer slice
const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearCustomerError: (state) => {
      state.error = null;
    },
    clearCustomer: (state) => {
      state.customer = null;
      state.addresses = [];
      state.contacts = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customers
      .addCase(fetchCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload.results || action.payload;
        // Handle pagination if API returns pagination data
        if (action.payload.count !== undefined) {
          state.pagination.totalCount = action.payload.count;
        }
        state.error = null;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch customers';
      })
      // Fetch single customer
      .addCase(fetchCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customer = action.payload;
        state.error = null;
      })
      .addCase(fetchCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch customer';
      })
      // Create customer
      .addCase(createCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers.unshift(action.payload);
        state.error = null;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create customer';
      })
      // Update customer
      .addCase(updateCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the customer in the list if it exists
        state.customers = state.customers.map((customer) =>
          customer.id === action.payload.id ? action.payload : customer
        );
        // Update the current customer if it's the one being viewed
        if (state.customer && state.customer.id === action.payload.id) {
          state.customer = action.payload;
        }
        state.error = null;
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update customer';
      })
      // Delete customer
      .addCase(deleteCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = state.customers.filter((customer) => customer.id !== action.payload);
        // Clear the current customer if it's the one being deleted
        if (state.customer && state.customer.id === action.payload) {
          state.customer = null;
        }
        state.error = null;
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete customer';
      })
      // Fetch customer addresses
      .addCase(fetchCustomerAddresses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerAddresses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.addresses = action.payload;
        state.error = null;
      })
      .addCase(fetchCustomerAddresses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch customer addresses';
      })
      // Fetch customer contacts
      .addCase(fetchCustomerContacts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerContacts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contacts = action.payload;
        state.error = null;
      })
      .addCase(fetchCustomerContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch customer contacts';
      });
  },
});

export const { clearCustomerError, clearCustomer } = customerSlice.actions;
export default customerSlice.reducer;

// Selectors
export const selectCustomers = (state) => ({
  customers: state.customers.customers,
  customer: state.customers.customer,
  addresses: state.customers.addresses,
  contacts: state.customers.contacts,
  loading: state.customers.isLoading,
  error: state.customers.error,
  pagination: {
    current: state.customers.pagination.page,
    pageSize: state.customers.pagination.pageSize,
    total: state.customers.pagination.totalCount,
  },
});
